import { App } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { useStore } from "jotai"
import { useMemo } from "react"

import {
  classAtoms,
  classSessionAtoms,
  makeupAtoms,
  studentAtoms,
} from "#atoms"
import GenericFormModal from "#blocks/Form/GenericFormModal"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import { MAKEUP_REASONS } from "#constants/makeupOptions"
import { useApiContext } from "#contexts/ApiContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import {
  AddMakeupReceipt,
  MAKEUP_RECEIPT_TYPES,
} from "#customTypes/composedTypes/emailTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type AddMakeupFormValues,
  MakeupConstructor,
  type MakeupWriteValues,
} from "#customTypes/composedTypes/makeupTypes"
import { Uuid } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import useAnalytics from "#hooks/useAnalytics"
import {
  formatDob,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  STANDARD_DAY_FORMAT,
  STANDARD_TIME_FORMAT,
  UNKNOWN_DOB,
} from "#utils/dateHelpers"
import { getStudentRecipients } from "#utils/emailHelpers"

import AddMakeupForm from "./AddMakeupForm"

const DEFAULT_MAKEUP_VALUES: AddMakeupFormValues = {
  id: undefined,
  createdAt: undefined,
  lastUpdatedAt: undefined,
  reason: MAKEUP_REASONS.PLANNED_ABSENCE,
  studentId: undefined,
  missedClassId: undefined,
  missedSessionId: undefined,
  makeupFilterDate: undefined,
  isMakeupScheduled: false,
  isScheduledForNextSemester: false,
  makeupClassId: undefined,
  makeupSessionId: undefined,
  semester: undefined,
}

type MakeupReceiptSource = Pick<
  AddMakeupFormValues,
  | "studentId"
  | "missedSessionId"
  | "missedClassId"
  | "makeupSessionId"
  | "makeupClassId"
  | "isMakeupScheduled"
  | "reason"
>

// GenericFormModal stamps audit fields onto the form payload before onFinish.
// makeupFilterDate is still the DatePicker value here; cleanFormData leaves Dayjs intact.
interface AddMakeupCleanedValues extends AddMakeupFormValues {
  createdAt: string
  createdBy: Uuid
  lastUpdatedAt: string
  lastUpdatedBy: Uuid
  reason: MAKEUP_REASONS
  studentId: Uuid
  missedClassId: Uuid
  missedSessionId: Uuid
}

const formatMakeupFilterDate = (value: Dayjs): string => {
  invariant(value.isValid() === true, "Makeup filter date is invalid!")
  return value.format(STANDARD_DATE_FORMAT)
}

export interface AddMakeupFormModalProps {
  onClose: () => void
  makeupId: Uuid | undefined
}

const AddMakeupFormModal = ({ onClose, makeupId }: AddMakeupFormModalProps) => {
  // Errors are handled in GenericFormModal
  const isEdit = makeupId != null
  const { trackMakeupAction } = useAnalytics()
  const { apiHandler } = useApiContext()
  const { modal, message } = App.useApp()
  const store = useStore()

  const { canCreate, canRead, canUpdate, canDelete } = useResourcePermissions(
    RESOURCES.MAKEUP
  )
  const makeup = useOneDoc<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    (canRead && makeupId) || NULL_ID
  )

  const initialValues = useMemo((): AddMakeupFormValues => {
    if (!makeup || !canRead) return DEFAULT_MAKEUP_VALUES

    // DatePicker values are Dayjs. Stored makeup fields stay on this object.
    return {
      ...makeup,
      makeupClassAndSessionId: makeup.isMakeupScheduled
        ? `${makeup.makeupClassId} ${makeup.makeupSessionId}`
        : "",
      makeupFilterDate:
        typeof makeup.makeupFilterDate === "string"
          ? dayjs(makeup.makeupFilterDate)
          : undefined,
    }
  }, [makeup, canRead])

  const handleSendReceipt = async (
    formValues: MakeupReceiptSource,
    isDelete: boolean = false
  ) => {
    const {
      studentId,
      missedSessionId,
      missedClassId,
      makeupSessionId,
      makeupClassId,
      isMakeupScheduled,
      reason,
    } = formValues

    const title = isDelete
      ? "Makeup deleted! Would you like to send a cancellation email?"
      : "Makeup saved! Would you like to send a confirmation email?"

    modal.confirm({
      okText: "Yes, send email",
      cancelText: "No",
      title,
      onOk: async () => {
        try {
          const selectedStudent = store.get(studentAtoms.docByIdAtom(studentId))

          if (!selectedStudent) {
            throw new Error("Student not found.")
          }

          const missedClass = store.get(classAtoms.docByIdAtom(missedClassId))
          const missedSession = store.get(
            classSessionAtoms.docByIdAtom(missedSessionId)
          )

          const makeupClass = store.get(classAtoms.docByIdAtom(makeupClassId))
          const makeupSession = store.get(
            classSessionAtoms.docByIdAtom(makeupSessionId)
          )

          invariant(missedClass != null, "Missed class not found.")
          invariant(missedSession != null, "Missed session not found.")
          invariant(
            isMakeupScheduled ? makeupClass != null : true,
            "Makeup class not found."
          )
          invariant(
            isMakeupScheduled ? makeupSession != null : true,
            "Makeup session not found."
          )

          const recipients = getStudentRecipients(selectedStudent)

          if (!recipients.length) {
            throw new Error("No recipients found.")
          }

          const receiptData: AddMakeupReceipt = {
            recipients,
            sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),

            Name: selectedStudent.name,
            DOB: formatDob(selectedStudent.dob) ?? UNKNOWN_DOB,
            ZoomLink:
              isMakeupScheduled && makeupClass ? makeupClass.zoomLink : null,

            ClassDate: dayjs(missedSession.eventStart).format(
              STANDARD_DATE_FORMAT
            ),
            ClassDay: `${dayjs(missedSession.eventStart).format(STANDARD_DAY_FORMAT)}${missedClass?.isSummerCamp ? " Camp" : ""}`,
            ClassStartTime: dayjs(missedSession.eventStart).format(
              STANDARD_TIME_FORMAT
            ),
            ClassEndTime: dayjs(missedSession.eventEnd).format(
              STANDARD_TIME_FORMAT
            ),

            MakeupClassDate:
              isMakeupScheduled && makeupSession
                ? dayjs(makeupSession.eventStart).format(STANDARD_DATE_FORMAT)
                : null,
            MakeupClassDay:
              isMakeupScheduled && makeupSession
                ? `${dayjs(makeupSession.eventStart).format(STANDARD_DAY_FORMAT)}${makeupClass?.isSummerCamp ? " Camp" : ""}`
                : null,
            MakeupClassEndTime:
              isMakeupScheduled && makeupSession
                ? dayjs(makeupSession.eventEnd).format(STANDARD_TIME_FORMAT)
                : null,
            MakeupClassStartTime:
              isMakeupScheduled && makeupSession
                ? dayjs(makeupSession.eventStart).format(STANDARD_TIME_FORMAT)
                : null,
          }

          let makeupType
          if (isDelete) {
            makeupType = MAKEUP_RECEIPT_TYPES.MAKEUP_CANCELLATION
          } else if (isMakeupScheduled) {
            if (isEdit) {
              makeupType = MAKEUP_RECEIPT_TYPES.MAKEUP_CHANGE
            } else {
              makeupType = MAKEUP_RECEIPT_TYPES.MAKEUP_CONFIRMATION
            }
          } else {
            if (reason === MAKEUP_REASONS.VACATION) {
              makeupType = MAKEUP_RECEIPT_TYPES.VACATION_ACKNOWLEDGEMENT
            } else {
              makeupType = MAKEUP_RECEIPT_TYPES.MAKEUP_TBD
            }
          }

          await apiHandler.sendReceiptEmail(receiptData, makeupType)
          message.success(
            isDelete ? "Cancellation email sent" : "Confirmation email sent"
          )
        } catch (error) {
          console.error("✅🔥 error => ", error)
        }
      },
      onCancel: () => {},
    })
  }

  const handleFinish = async (
    cleanedValues: AddMakeupCleanedValues
  ): Promise<void> => {
    if (!canCreate && !isEdit) {
      throw new Error("You do not have permission to create makeups")
    }
    if (!canUpdate && isEdit) {
      throw new Error("You do not have permission to update makeups")
    }

    const [makeupClassId, makeupSessionId] =
      cleanedValues.makeupClassAndSessionId?.split(" ") ?? []
    const rawMakeupFilterDate = cleanedValues.makeupFilterDate
    const formattedFilterDate = dayjs.isDayjs(rawMakeupFilterDate)
      ? formatMakeupFilterDate(rawMakeupFilterDate)
      : typeof rawMakeupFilterDate === "string"
        ? rawMakeupFilterDate
        : undefined
    const isMakeupScheduled = cleanedValues.isMakeupScheduled
    const savedMakeupClassId = isMakeupScheduled ? makeupClassId : null
    const savedMakeupSessionId = isMakeupScheduled ? makeupSessionId : null

    // Null when the makeup is not scheduled or the picker was cleared. The next
    // open treats a missing date as "today forward" instead of reusing this one.
    const savedMakeupFilterDate =
      isMakeupScheduled && typeof formattedFilterDate === "string"
        ? formattedFilterDate
        : null

    const submitData: MakeupWriteValues = {
      id: cleanedValues.id,
      createdAt: cleanedValues.createdAt,
      createdBy: cleanedValues.createdBy,
      lastUpdatedAt: cleanedValues.lastUpdatedAt,
      lastUpdatedBy: cleanedValues.lastUpdatedBy,
      reason: cleanedValues.reason,
      studentId: cleanedValues.studentId,
      missedClassId: cleanedValues.missedClassId,
      missedSessionId: cleanedValues.missedSessionId,
      semester: cleanedValues.semester,
      isMakeupScheduled,
      makeupClassId: savedMakeupClassId,
      makeupSessionId: savedMakeupSessionId,
      ...(typeof cleanedValues.isScheduledForNextSemester === "boolean"
        ? {
            isScheduledForNextSemester:
              cleanedValues.isScheduledForNextSemester,
          }
        : {}),
      makeupFilterDate: savedMakeupFilterDate,
    }

    const savedMakeupId = await Firebase.getInstance().createOrUpdateMakeup(
      submitData,
      initialValues
    )
    invariant(savedMakeupId != null, "Saved makeup is missing an id!")

    trackMakeupAction(ANALYTICS_EVENTS.MAKEUP_SCHEDULED, {
      makeupId: savedMakeupId,
      studentId: cleanedValues.studentId,
      missedClassId: cleanedValues.missedClassId,
      missedSessionId: cleanedValues.missedSessionId,
      isMakeupScheduled,
      makeupClassId: savedMakeupClassId,
      makeupSessionId: savedMakeupSessionId,
    })

    await handleSendReceipt({
      studentId: cleanedValues.studentId,
      missedSessionId: cleanedValues.missedSessionId,
      missedClassId: cleanedValues.missedClassId,
      makeupSessionId: savedMakeupSessionId,
      makeupClassId: savedMakeupClassId,
      isMakeupScheduled,
      reason: cleanedValues.reason,
    })
  }

  const handleDelete = async () => {
    try {
      if (!canDelete) {
        throw new Error("You do not have permission to delete makeups")
      }

      if (initialValues.id == null) return

      const studentId = initialValues.studentId
      const missedSessionId = initialValues.missedSessionId
      const missedClassId = initialValues.missedClassId
      invariant(
        studentId != null,
        `Data for _student_id ${studentId} is missing!`
      )
      invariant(
        missedSessionId != null,
        `Data for _missed_session_id ${missedSessionId} is missing!`
      )
      invariant(
        missedClassId != null,
        `Data for _missed_class_id ${missedClassId} is missing!`
      )

      // Defensive: another makeup doc could claim this same missed-session slot
      // (legacy/corrupt data, or a parallel edit). deleteMakeup keeps the slot
      // absent while one remains instead of reverting to ENROLLED.
      const sameSlotMakeups =
        await Firebase.getInstance().variableQuery<MakeupConstructor>(
          DOC_TYPES.MAKEUPS,
          {
            queries: {
              studentId: {
                value: studentId,
                comparator: COMPARATORS.EQUALS,
              },
              missedSessionId: {
                value: missedSessionId,
                comparator: COMPARATORS.EQUALS,
              },
            },
          }
        )

      await Firebase.getInstance().deleteMakeup({
        makeupId: initialValues.id,
        studentId,
        missedSessionId,
        makeupSessionId: initialValues.makeupSessionId,
        sameSlotMakeupIds: sameSlotMakeups.map(({ id }) => id),
      })

      trackMakeupAction(ANALYTICS_EVENTS.MAKEUP_CANCELLED, {
        makeupId: initialValues.id,
        studentId,
        missedClassId,
        missedSessionId,
        makeupClassId: initialValues.makeupClassId,
        makeupSessionId: initialValues.makeupSessionId,
        isMakeupScheduled: initialValues.isMakeupScheduled,
      })

      await handleSendReceipt(initialValues, true)

      // Brief delay to allow Firebase listeners to process the deletion
      setTimeout(() => {
        onClose()
      }, 500)
    } catch (error) {
      console.error("✅🔥 error => ", error)
      modal.error({
        title: "Error deleting makeup",
        content: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const student = useGetOneStudent(initialValues?.studentId)

  return (
    <GenericFormModal<AddMakeupFormValues, AddMakeupCleanedValues>
      title={isEdit && student ? `Makeup for ${student.name}` : "Add Makeup"}
      onClose={onClose}
      initialValues={initialValues}
      onFinish={handleFinish}
      resource={RESOURCES.MAKEUP}
      onDelete={handleDelete}
    >
      <AddMakeupForm initialValues={initialValues} />
    </GenericFormModal>
  )
}

export default AddMakeupFormModal
